'use es6';

import { List } from 'immutable';
import { COLUMN_ROLES } from 'reporting-data/constants/relationalReports';
import { getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
import { Axis, AxisTypes, MarkTypes, XYVisualization } from 'reporting-visualizations/visualizations/xy/xyRecords';
import { toVisualizationOrder } from '../../modify/sorts/report-sort-utils';
import { getDateResolution, getReportColorEncoding, getReportDetailEncoding, getReportSizingEncoding, getReportXEncoding, getReportYEncoding } from '../../utils/report-utils';
import { getEncodingFormatParameters, getEncodingFormatType } from '../../utils/visual-utils';
import { applyScaleToAxis } from '../apply-scale-to-axis';
import { applyTypeToAxis } from '../apply-type-to-axis';
import { computeAssignments } from '../color/assign';
import { collectDimensionBuckets, collectMeasureBuckets } from '../color/bucket';
import { getColorPalette } from '../color/utils';
import { getDeprecatedDefaultPalettes } from '../color/palettes';

/**
 * @param {Column?} colorColumn
 * @param {Column} measureColumn
 * @param {Dataset} dataset
 * @param {ColorOption} colorOption
 * @param {BrandKitSnowflakePalettes} brandKitPalettes
 * @param {ReportingPalettes} palettes
 * @returns high charts color encoding
 */
const toColor = (colorColumn, measureColumn, dataset, colorOption, color, brandKitPalettes, palettes) => {
  const order = colorColumn && toVisualizationOrder(colorColumn, dataset);
  const buckets = colorColumn === undefined && measureColumn ? collectMeasureBuckets(List.of(measureColumn), dataset) : collectDimensionBuckets(colorColumn, dataset);
  const {
    scheme
  } = getColorPalette(colorOption, brandKitPalettes, (palettes === null || palettes === void 0 ? void 0 : palettes.DEFAULT_PALETTES) || getDeprecatedDefaultPalettes('scatter-mapper-fallback'), palettes === null || palettes === void 0 ? void 0 : palettes.DefaultCategorical);
  const assignments = computeAssignments(buckets, scheme, colorOption.assignments);
  if (assignments.isEmpty()) {
    return undefined;
  }
  if (colorColumn === undefined) {
    return {
      default: assignments.first()
    };
  }
  const colorId = colorColumn.alias;
  return {
    field: colorId,
    defined: assignments.toJS(),
    order,
    formattingType: getEncodingFormatType(color),
    formattingObject: getEncodingFormatParameters(color)
  };
};
export const mapScatterVisual = (report, datasets, reportOptions, {
  brandKitPalettes,
  palettes
}) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const x = getReportXEncoding(report);
  const y = getReportYEncoding(report);
  const color = getReportColorEncoding(report);
  const sizing = getReportSizingEncoding(report);
  const detail = getReportDetailEncoding(report);
  const {
    showBubbleLegend,
    showDataLabels,
    xMinMax,
    yMinMax,
    yAxisType,
    xAxisType,
    color: colorOption,
    legendPosition,
    legendWidth,
    opacity,
    dataLabelFormat
  } = report.visual.options;
  const yAxisKey = 'left';
  const xAxisKey = 'bottom';
  const xField = x && x.column.alias;
  const yField = y && y.column.alias;
  const colorField = color && color.column.alias;
  const sizingField = sizing && sizing.column.alias;
  const detailField = detail && detail.column.alias;
  const xRoleIsDimension = x && x.column.role === COLUMN_ROLES.DIMENSION;
  const yRoleIsDimension = y && y.column.role === COLUMN_ROLES.DIMENSION;
  const maybeDateResolution = getDateResolution(detailField, report) || getDateResolution(xField, report) || getDateResolution(yField, report);
  const primaryLayer = {
    dataset: 'primary',
    mark: {
      type: sizing ? MarkTypes.bubble : MarkTypes.scatter,
      marker: {
        enabled: true
      }
    },
    x: x ? {
      type: xRoleIsDimension ? AxisTypes.category : AxisTypes.linear,
      field: xField,
      blend: xAxisKey,
      order: toVisualizationOrder(x.column, primaryDataset),
      fill: false,
      formattingType: getEncodingFormatType(x),
      formattingObject: getEncodingFormatParameters(x)
    } : {
      type: AxisTypes.scaffold,
      blend: xAxisKey
    },
    y: y ? {
      type: yRoleIsDimension ? AxisTypes.category : AxisTypes.linear,
      field: yField,
      blend: yAxisKey,
      fill: false,
      formattingType: getEncodingFormatType(y),
      formattingObject: getEncodingFormatParameters(y)
    } : {
      type: AxisTypes.scaffold,
      blend: yAxisKey
    },
    sizing: sizing ? {
      field: sizingField,
      formattingType: getEncodingFormatType(sizing),
      formattingObject: getEncodingFormatParameters(sizing)
    } : undefined,
    detail: detail ? {
      field: detailField,
      formattingType: getEncodingFormatType(detail),
      formattingObject: getEncodingFormatParameters(detail)
    } : undefined,
    color: toColor(report.columns.get(colorField), report.columns.get(yField), primaryDataset, colorOption, color, brandKitPalettes, palettes)
  };
  const layers = [primaryLayer];
  const vizConfig = {
    type: 'XY',
    data: {
      primary: primaryDataset
    },
    style: {
      showBubbleLegend,
      dataLabels: showDataLabels,
      dataLabelFormat,
      legendPosition,
      legendWidth,
      opacity,
      x: x && !xRoleIsDimension ? {
        [xAxisKey]: applyTypeToAxis(applyScaleToAxis(Axis({}), xMinMax), xAxisType)
      } : undefined,
      y: y && !yRoleIsDimension ? {
        [yAxisKey]: applyTypeToAxis(applyScaleToAxis(Axis({}), yMinMax), yAxisType)
      } : undefined,
      dateResolution: maybeDateResolution
    },
    layers
  };
  return XYVisualization(vizConfig);
};